'use client'

import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/hooks/use-auth'

// Service modes (`lib/billing/services/shopify-insights-access.ts`) plus
// two hook-only states emitted when the fetch itself fails.
//
// Semantics:
//   - `unlimited`     → plan grants Insights without a time limit
//                       (`shopify_insights_trial_duration_hours = null`).
//   - `trial_active`  → finite trial in progress.
//   - `trial_expired` → finite trial ended.
//   - `trial_not_started` → finite trial nobody has opened (a super user viewing the tenant).
//   - `not_available` → no subscription / no active integration.
//   - `forbidden` / `error` → hook-only states for fetch failures.
export type ShopifyInsightsMode =
  | 'unlimited'
  | 'trial_active'
  | 'trial_expired'
  | 'trial_not_started'
  | 'not_available'
  | 'forbidden'
  | 'error'

interface ShopifyInsightsAccess {
  allowed: boolean
  mode: ShopifyInsightsMode
  trialExpiresAt: string | null
  loading: boolean
}

/**
 * Hook for Shopify Insights gated pages (dashboard, products).
 * Starts trial on first access if `startTrial=true`.
 */
export function useShopifyInsightsAccess(startTrial = false): ShopifyInsightsAccess {
  const { fetchWithAuth } = useAuth()
  const [state, setState] = useState<ShopifyInsightsAccess>({
    allowed: false,
    mode: 'not_available',
    trialExpiresAt: null,
    loading: true,
  })

  const check = useCallback(async () => {
    try {
      const url = startTrial
        ? '/api/billing/shopify-insights?start=true'
        : '/api/billing/shopify-insights'
      const res = await fetchWithAuth(url)
      if (!res.ok) {
        const mode = (res.status === 401 || res.status === 403) ? 'forbidden' : (res.status >= 500 ? 'error' : 'not_available')
        setState({ allowed: false, mode, trialExpiresAt: null, loading: false })
        return
      }
      const data = await res.json()
      setState({
        allowed: data.allowed,
        mode: data.mode,
        trialExpiresAt: data.trialExpiresAt ?? null,
        loading: false,
      })
    } catch {
      setState({ allowed: false, mode: 'error', trialExpiresAt: null, loading: false })
    }
  }, [fetchWithAuth, startTrial])

  useEffect(() => { check() }, [check])

  return state
}
